import { useState, useCallback } from 'react';
import { createContainer } from 'unstated-next';
import { useDataProvider, useGetOne } from 'react-admin';
import {
  RegionDto,
  RoleDto,
  UserDto,
  UserRegionInterface,
  UserRoleInterface,
} from '@vatos-pas/common';
import { AppRole } from 'providers/roles';
import { getSupervisors } from 'services/regions';

/**
 * User Container - Data Access Layer
 *
 * ARCHITECTURE NOTE: This container acts as a pluggable adapter between the
 * data layer and the module's components. Components (UserForm, views) remain
 * data-layer agnostic - they only consume this container's interface. This
 * allows swapping the data layer (e.g., react-admin, react-query, Apollo, or
 * plain fetch) without changing component code.
 *
 * SWR BEHAVIOR: Uses react-admin's useGetOne which caches data in Redux store.
 * Cached data is returned immediately while fresh data is fetched in the
 * background. Loading spinner only shows on first load when no cached data
 * exists. Cache persists across navigations via Redux.
 */

export interface RoleOption {
  id: string;
  name: string;
}

export interface RegionOption {
  id: string;
  name: string;
}

export interface UserFormValues {
  firstName: string;
  lastName: string;
  email: string;
  primaryPhone: string;
  idNumber: string;
  password?: string;
  active: boolean;
  selectedRoles: string[];
  selectedRegions: string[];
}

export interface SupervisorOption {
  id: string;
  name: string;
}

function useUser() {
  const dataProvider = useDataProvider();

  const [userId, setUserId] = useState<string>('');
  const {
    data: selectedUser,
    loading: userLoading,
    loaded: userLoaded,
    error: userError,
  } = useGetOne<UserDto>('user', userId, { enabled: !!userId });

  const isLoading = !!userId && !selectedUser && userLoading;
  const isRevalidating = !!userId && userLoading;

  const [formLoading, setFormLoading] = useState(false);
  const [supervisors, setSupervisors] = useState<SupervisorOption[]>([]);
  const [supervisorRoleId, setSupervisorRoleId] = useState<string>('');
  const [currentUserRegion, setCurrentUserRegion] =
    useState<UserRegionInterface | null>(null);
  const [userRoles, setUserRoles] = useState<UserRoleInterface[]>([]);
  const [userRegions, setUserRegions] = useState<UserRegionInterface[]>([]);
  const [userRolesLoaded, setUserRolesLoaded] = useState(false);
  const [userRegionsLoaded, setUserRegionsLoaded] = useState(false);

  const loadUserById = useCallback((id: string): void => {
    setUserId(id);
  }, []);

  const createUser = useCallback(
    async (
      data: Omit<UserFormValues, 'selectedRoles' | 'selectedRegions'>,
    ): Promise<UserDto> => {
      setFormLoading(true);
      try {
        const result = await dataProvider.create<UserDto>('user', { data });
        const user = result.data;
        setUserId(user.id);
        return user;
      } finally {
        setFormLoading(false);
      }
    },
    [dataProvider],
  );

  const updateUser = useCallback(
    async (
      id: string,
      data: Partial<Omit<UserFormValues, 'selectedRoles' | 'selectedRegions'>>,
    ): Promise<UserDto> => {
      if (!selectedUser) {
        throw new Error('Cannot update: user data not loaded');
      }
      setFormLoading(true);
      try {
        const result = await dataProvider.update<UserDto>('user', {
          id,
          data: { ...selectedUser, ...data },
          previousData: selectedUser,
        });
        // dataProvider.update updates Redux store automatically
        // selectedUser will update via useGetOne
        return result.data;
      } finally {
        setFormLoading(false);
      }
    },
    [dataProvider, selectedUser],
  );

  // loadOptions-compatible function for Autocomplete (lazy loading on focus)
  const loadRoleOptions = useCallback(
    async (_searchTerm: string): Promise<RoleOption[]> => {
      try {
        const result = await dataProvider.getList<RoleDto>('role', {
          filter: {},
          pagination: { page: 1, perPage: 200 },
          sort: { field: 'name', order: 'ASC' },
        });
        return result.data.map(role => ({
          id: role.id,
          name: role.name,
        }));
      } catch (error) {
        console.error('Failed to load roles:', error);
        return [];
      }
    },
    [dataProvider],
  );

  // loadOptions-compatible function for Autocomplete (lazy loading on focus)
  const loadRegionOptions = useCallback(
    async (_searchTerm: string): Promise<RegionOption[]> => {
      try {
        const result = await dataProvider.getList<RegionDto>('region', {
          filter: {},
          pagination: { page: 1, perPage: 999 },
          sort: { field: 'name', order: 'ASC' },
        });
        return result.data.map(region => ({
          id: region.id,
          name: region.name,
        }));
      } catch (error) {
        console.error('Failed to load regions:', error);
        return [];
      }
    },
    [dataProvider],
  );

  const findInactiveRegion =
    useCallback(async (): Promise<RegionOption | null> => {
      try {
        const result = await dataProvider.getList<RegionDto>('region', {
          filter: { 'name||$eq': 'Inactive User' },
          pagination: { page: 1, perPage: 1 },
          sort: { field: 'name', order: 'ASC' },
        });
        if (result.data[0]) {
          return { id: result.data[0].id, name: result.data[0].name };
        }
        return null;
      } catch (error) {
        console.error('Failed to find inactive region:', error);
        return null;
      }
    }, [dataProvider]);

  const loadSupervisorsForRegion = useCallback(
    async (regionId: string): Promise<void> => {
      try {
        const response = await getSupervisors({ regionId });
        setSupervisors(
          response.data.data.map(({ id, firstName, lastName }) => ({
            id,
            name: `${firstName} ${lastName}`,
          })),
        );
      } catch (error) {
        console.error('Failed to load supervisors:', error);
        setSupervisors([]);
      }
    },
    [],
  );

  const loadSupervisorRoleId = useCallback(async (): Promise<void> => {
    try {
      const result = await dataProvider.getList<RoleDto>('role', {
        filter: {},
        pagination: { page: 1, perPage: 20 },
        sort: { field: 'id', order: 'ASC' },
      });

      const supervisorRole = result.data.find(
        role => role.name === AppRole.Supervisor,
      );
      if (supervisorRole) {
        setSupervisorRoleId(supervisorRole.id);
      }
    } catch (error) {
      console.error('Failed to load supervisor role ID:', error);
      setSupervisorRoleId('');
    }
  }, [dataProvider]);

  const loadUserRegion = useCallback(
    async (userId: string): Promise<void> => {
      try {
        const result = await dataProvider.getList<UserRegionInterface>(
          'user-region',
          {
            filter: { userId },
            pagination: { page: 1, perPage: 200 },
            sort: { field: 'id', order: 'ASC' },
          },
        );

        if (result.data[0]) {
          setCurrentUserRegion(result.data[0]);
        }
      } catch (error) {
        console.error('Failed to load user region:', error);
        setCurrentUserRegion(null);
      }
    },
    [dataProvider],
  );

  const loadUserRolesForUser = useCallback(
    async (userId: string): Promise<UserRoleInterface[]> => {
      try {
        const result = await dataProvider.getList<UserRoleInterface>(
          'user-role',
          {
            filter: { userId },
            pagination: { page: 1, perPage: 200 },
            sort: { field: 'id', order: 'DESC' },
          },
        );
        setUserRoles(result.data);
        setUserRolesLoaded(true);
        return result.data;
      } catch (error) {
        console.error('Failed to load user roles:', error);
        setUserRoles([]);
        setUserRolesLoaded(true);
        return [];
      }
    },
    [dataProvider],
  );

  const loadUserRegionsForUser = useCallback(
    async (userId: string): Promise<UserRegionInterface[]> => {
      try {
        const result = await dataProvider.getList<UserRegionInterface>(
          'user-region',
          {
            filter: { userId },
            pagination: { page: 1, perPage: 200 },
            sort: { field: 'id', order: 'DESC' },
          },
        );
        setUserRegions(result.data);
        setUserRegionsLoaded(true);
        return result.data;
      } catch (error) {
        console.error('Failed to load user regions:', error);
        setUserRegions([]);
        setUserRegionsLoaded(true);
        return [];
      }
    },
    [dataProvider],
  );

  const updateUserRegionManager = useCallback(
    async (userRegionId: string, managerUserId: string): Promise<void> => {
      try {
        const userRegion = await dataProvider.getOne<UserRegionInterface>(
          'user-region',
          { id: userRegionId },
        );

        await dataProvider.update('user-region', {
          id: userRegionId,
          data: { managerUserId },
          previousData: userRegion.data,
        });
      } catch (error) {
        console.error('Failed to update user region manager:', error);
        throw error;
      }
    },
    [dataProvider],
  );

  const createUserRole = useCallback(
    async (userId: string, roleId: string): Promise<void> => {
      await dataProvider.create('user-role', {
        data: { userId, roleId },
      });
    },
    [dataProvider],
  );

  const deleteUserRole = useCallback(
    async (userRoleId: string): Promise<void> => {
      await dataProvider.delete('user-role', {
        id: userRoleId,
        previousData: { id: userRoleId },
      });
    },
    [dataProvider],
  );

  const createUserRegion = useCallback(
    async (userId: string, regionId: string): Promise<void> => {
      await dataProvider.create('user-region', {
        data: { userId, regionId },
      });
    },
    [dataProvider],
  );

  const deleteUserRegion = useCallback(
    async (userRegionId: string): Promise<void> => {
      await dataProvider.delete('user-region', {
        id: userRegionId,
        previousData: { id: userRegionId },
      });
    },
    [dataProvider],
  );

  const batchUpdateUserRoles = useCallback(
    async (
      userId: string,
      existingUserRoles: UserRoleInterface[],
      newRoleIds: string[],
    ): Promise<void> => {
      // Find roles to add
      const existingRoleIds = existingUserRoles.map(ur => ur.roleId);
      const rolesToAdd = newRoleIds.filter(id => !existingRoleIds.includes(id));
      const rolesToDelete = existingUserRoles.filter(
        ur => !newRoleIds.includes(ur.roleId),
      );

      // Execute deletions
      const deletePromises = rolesToDelete.map(ur => deleteUserRole(ur.id));
      await Promise.all(deletePromises);

      // Execute additions
      const addPromises = rolesToAdd.map(roleId =>
        createUserRole(userId, roleId),
      );
      await Promise.all(addPromises);
    },
    [createUserRole, deleteUserRole],
  );

  const batchUpdateUserRegions = useCallback(
    async (
      userId: string,
      existingUserRegions: UserRegionInterface[],
      newRegionIds: string[],
    ): Promise<void> => {
      // Find regions to add
      const existingRegionIds = existingUserRegions.map(ur => ur.regionId);
      const regionsToAdd = newRegionIds.filter(
        id => !existingRegionIds.includes(id),
      );
      const regionsToDelete = existingUserRegions.filter(
        ur => !newRegionIds.includes(ur.regionId),
      );

      // Additions first: the API refuses removing the last Region of a Regional Admin,
      // so a swap must add the new Region before dropping the old one.
      const addPromises = regionsToAdd.map(regionId =>
        createUserRegion(userId, regionId),
      );
      await Promise.all(addPromises);

      const deletePromises = regionsToDelete.map(ur => deleteUserRegion(ur.id));
      await Promise.all(deletePromises);
    },
    [createUserRegion, deleteUserRegion],
  );

  const resetLoadedFlags = useCallback(() => {
    setUserRolesLoaded(false);
    setUserRegionsLoaded(false);
  }, []);

  return {
    // Primary user state (SWR pattern via useGetOne + Redux)
    selectedUser,
    isLoading, // true only on first load (no cache in Redux)
    isRevalidating, // true during any fetch
    userLoaded, // true once fetch completed (use to detect "not found")
    userError, // truthy on fetch failure (404, network, etc.)
    // Secondary state
    formLoading,
    supervisors,
    supervisorRoleId,
    currentUserRegion,
    userRoles,
    userRegions,
    userRolesLoaded,
    userRegionsLoaded,
    // Actions
    loadUserById,
    createUser,
    updateUser,
    loadRoleOptions,
    loadRegionOptions,
    findInactiveRegion,
    loadSupervisorsForRegion,
    loadSupervisorRoleId,
    loadUserRegion,
    loadUserRolesForUser,
    loadUserRegionsForUser,
    updateUserRegionManager,
    batchUpdateUserRoles,
    batchUpdateUserRegions,
    setFormLoading,
    resetLoadedFlags,
  };
}

export const UserContainer = createContainer(useUser);
